import { Transforms, Element, Editor } from 'slate';
import type { SlateBlock, ImageBlock } from '@atlas/renderer';
import filestack from '@atlas/tool/box/file/filestack';
import isVoidBlock from './isVoidBlock';
import insertImageNode from './insertImageNode';
import fileResponseToUploadedAttachment from '@atlas/tool/box/file/fileResponseToUploadedAttachment';

export default function withImages(
    editor: Editor,
    defaults: { align?: ImageBlock['align'] } = {},
    options?: {
        maxImageHeight?: number;
        maxImageWidth?: number;
        onError?: (message: string) => void;
        setUploading?: (imagesCount: number) => void;
    },
): Editor {
    const { isVoid, insertData } = editor;

    const onError = options?.onError;
    const maxImageHeight = options?.maxImageHeight;
    const maxImageWidth = options?.maxImageWidth;

    editor.maxImageHeight = maxImageHeight;
    editor.maxImageWidth = maxImageWidth;

    editor.isVoid = (element: SlateBlock) => isVoidBlock(element) || isVoid(element);

    editor.insertData = (data) => {
        const html = data.getData('text/html');

        if (html && html.match(IMG_TAG_REGEX) === null) {
            return insertData(data);
        }

        const { files } = data;
        if (!files) return insertData(data);

        const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
        if (!imageFiles.length) return insertData(data);

        Array.from(imageFiles).forEach((file) => {
            const fingerprint = Math.random().toString();
            insertImageNode(editor, {
                fingerprint,
                width: defaults.align === 'standard' ? 257 : undefined,
                align: defaults.align,
            });
            options?.setUploading?.(1);
            filestack()
                .then((client) =>
                    client
                        .upload(file, {}) // The version 3.33.3 fails without the second argument
                        .then(fileResponseToUploadedAttachment)
                        .then(async (uploadedFile) => {
                            const [realWidth, realHeight] = await new Promise<[number, number]>((resolve, reject) => {
                                const img = new Image();
                                img.onload = () => resolve([img.width, img.height]);
                                img.onerror = reject;
                                img.src = URL.createObjectURL(file);
                            });

                            const imageIndex = editor.children.findIndex(
                                (node) =>
                                    Element.isElement(node) &&
                                    node.type === 'image' &&
                                    node.fingerprint === fingerprint,
                            );
                            if (imageIndex === -1) return;

                            let visibleWidth: number | undefined = maxImageHeight
                                ? realHeight > maxImageHeight
                                    ? Math.round(realWidth * (maxImageHeight / realHeight))
                                    : realWidth
                                : undefined;
                            if (maxImageWidth) {
                                visibleWidth = Math.min(visibleWidth ?? Infinity, realWidth, maxImageWidth);
                            }

                            Transforms.setNodes(
                                editor,
                                {
                                    fileName: file.name,
                                    fingerprint: undefined,
                                    handle: uploadedFile.handle,
                                    originalHeight: realHeight,
                                    originalWidth: realWidth,
                                    src: uploadedFile.url,
                                    width: visibleWidth,
                                },
                                { at: [imageIndex] },
                            );
                        }),
                )
                .catch((error) => {
                    console.error('Inline image upload error: ', error);
                    console.warn('File: ', file);
                    onError?.(`Failed to upload image: ${file.name}`);

                    const imageIndex = editor.children.findIndex(
                        (node) => Element.isElement(node) && node.type === 'image' && node.fingerprint === fingerprint,
                    );
                    if (imageIndex === -1) return;
                    Transforms.removeNodes(editor, { at: [imageIndex] });
                })
                .finally(() => options?.setUploading?.(-1));
        });

        if (imageFiles.length >= files.length) return;

        const newData = new DataTransfer();
        Array.from(files).forEach((file) => imageFiles.includes(file) || newData.items.add(file));

        return insertData(newData);
    };

    return editor;
}

const IMG_TAG_REGEX = /<img[^>]*src="([^"]+)"[^>]*>/i;

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('withImages', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
